import { Flame, Trophy } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';

interface FocusStreakDisplayProps {
  userId: string;
}

interface FocusStreakData {
  current_streak: number;
  longest_streak: number;
  total_completed_sessions: number;
  last_focus_date: string | null;
}

export function FocusStreakDisplay({ userId }: FocusStreakDisplayProps) {
  const { data: streakData, isLoading } = useQuery({
    queryKey: ['focus-streak', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('focus_streaks')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      return data as FocusStreakData | null;
    },
    enabled: !!userId,
  });

  if (isLoading) {
    return (
      <Card className="p-4">
        <Skeleton className="h-20 w-full" />
      </Card>
    );
  }

  const currentStreak = streakData?.current_streak || 0;
  const longestStreak = streakData?.longest_streak || 0;
  const totalSessions = streakData?.total_completed_sessions || 0;

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-muted-foreground">Focus Streak</h3>
        <Flame className="w-5 h-5 text-primary" />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold font-heading text-foreground">
              {currentStreak}
            </span>
            <span className="text-lg text-muted-foreground">days</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Current streak
          </p>
        </div>

        <div className="flex flex-col items-end gap-3">
          <div className="text-right">
            <div className="flex items-center gap-1">
              <Trophy className="w-4 h-4 text-secondary" />
              <span className="font-semibold text-foreground">{longestStreak}</span>
            </div>
            <p className="text-xs text-muted-foreground">Best streak</p>
          </div>
        </div>
      </div>

      {currentStreak > 0 && (
        <div className="pt-2 border-t border-border">
          <p className="text-xs text-center text-muted-foreground">
            🔥 You're on fire! Complete a session today to keep it going!
          </p>
        </div>
      )}

      {currentStreak === 0 && totalSessions > 0 && (
        <div className="pt-2 border-t border-border">
          <p className="text-xs text-center text-muted-foreground">
            Start a new streak today! 💪
          </p>
        </div>
      )}
    </Card>
  );
}
